import React, {useState} from 'react';
import {connect, DispatchProp} from "react-redux";
import {useTranslation, withTranslation} from "react-i18next";
import {RootState} from "../../../reducers";
import {getStoreConfig} from "../../services/Store/reducer";
import {getCart} from "../../services/Cart/reducer";
import {useCart} from "../../services/Cart/cartService";
import Money from "../Common/Money";
import {CartState} from "../../services/Cart/types";
import ResponsiveComponent from "../Common/ResponsiveComponent";
import CartTipModal from "./CartTipModal";

interface OwnProps {
    errors: any[],
    onChange: (cart: any) => void,
    data: any
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const getCartTip = (cart: CartState) => {
    const tipPromotion = cart.promotions.filter((x: any) => x.type === "TIP_MANUALLY")[0];
    return tipPromotion && tipPromotion.value_money ? tipPromotion.value_money.amount : 0;
}

const CartTipComponent = (props: Props) => {
    const cart = props.cart;
    let tipAmount = getCartTip(cart);
    const tipPercent = cart.total_price_without_tips.amount ? Math.round((tipAmount / cart.total_price_without_tips.amount) * 100) : 0;
    let customTip = ![0, 10, 15, 20].includes(tipPercent) || (!cart.total_price_without_tips.amount && tipAmount > 0);

    const [loading, setLoading] = useState(false);
    const [changeAmount, setChangeAmount] = useState<boolean>(false);
    const [customAmount, setCustomAmount] = useState<boolean>(customTip);
    const [customAmountModal, setCustomAmountModal] = useState<boolean>(false);
    const {t} = useTranslation();
    const cartService = useCart();
    const currency = cart.total_price_without_tips.currency;
    if (!changeAmount && tipAmount === 0) tipAmount = undefined;

    const addCustomTip = async (tipAmount?: number) => {
        await addTip(tipAmount)
        setChangeAmount(true);
        setCustomAmount(false);
    }

    const addDefineTip = async (tipAmount: number) => {
        await addTip(tipAmount);
        setChangeAmount(true);
        setCustomAmount(false);
    }

    const addTip = async (tipAmount: any) => {
        setLoading(true)
        try {
            await cartService.updateTip({
                amount: tipAmount
            });
            setLoading(false)
        } catch (error: any) {
            setLoading(false)
        }
    }

    const handleOnKeyDown = async (event:  React.KeyboardEvent<HTMLLabelElement>, tip: Record<string, any> ) => {
        if(event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (tip.percent === -1) {
                setCustomAmountModal(true);
            }
            else {
                await addDefineTip(tip.price.amount);
            }
        }


    }


    const renderTips = (sortedTips: number[]) => {
        const tips = sortedTips.map(x => {
            if (x === -1) {
                return {
                    price: {amount: -1, currency},
                    percent: -1,
                    selected: false,
                };
            }

            const xAmount = {amount: (cart.total_price_without_tips.amount * x) / 100, currency};
            let selectedTip = tipAmount ? tipAmount.toFixed(2) === xAmount.amount.toFixed(2) : false;


            if (x === 0) {
                selectedTip = tipAmount === x;

                return {
                    price: {amount: 0, currency},
                    percent: 0,
                    selected: selectedTip,
                };
            }


            return {
                percent: x,
                price: xAmount,
                selected: selectedTip
            }
        });

        return (
            <>
                {tips.map(tip => {
                    if (tip.percent === -1) return (
                        <label onClick={() => setCustomAmountModal(true)} key={tip.percent} tabIndex={0} onKeyDown={(event) => handleOnKeyDown(event, tip)}
                               className={`btn btn-default btn-lg btn-check-full btn-spinner${(props.cart.pending || loading) ? " loading" : ""}${customTip ? " active" : ""}`}>
                            <span className="mx-2 text-center">
                                <strong>{tipAmount && customTip ?
                                    <Money value={tipAmount}/> : t("modules.cart.field.other_tip.title")}</strong>
                                <small
                                    className="mt-1">{tipAmount && customTip ? t("modules.cart.action.change_tip_amount.title") : t("common.action.choose")}</small>
                            </span>
                            <div className="spinner-border spinner-border-sm"/>
                        </label>
                    )

                    return (
                        <label key={tip.percent} tabIndex={0} onKeyDown={(event) => handleOnKeyDown(event, tip)}
                               className={`btn btn-default btn-lg btn-check-full btn-spinner${(props.cart.pending || loading) ? " loading" : ""}${(tip.selected) ? " active" : ""}`}>
                                    <span className="mx-2 text-center">
                                        {tip.percent === 0 ? (
                                            <strong>{t("modules.cart.field.not_tip.title")}</strong>
                                        ) : (
                                            <>
                                                <strong><Money value={tip.price}/></strong>
                                                <small className="mt-1">
                                                    {tip.percent ? `${tip.percent}%` : t("common.word.lack")}
                                                </small>
                                            </>
                                        )}
                                    </span>
                            <input className="btn-check" type="radio" name="tip_amount"
                                   value={tip.price.amount} tabIndex={-1}
                                   autoComplete="off"
                                   checked={(tip.selected && !customAmount)}
                                   onChange={() => addDefineTip(tip.price.amount)}/>
                            <div className="spinner-border spinner-border-sm"/>
                        </label>
                    )
                })}
            </>
        )
    }
    {t("modules.cart.payment.header.title")}

    const renderTipTitle = () => {
        if(props.cart.type === "DELIVERY") return t("modules.cart.tip.header_delivery.title");
        if(props.cart.type === "DINE_IN" && props.cart.number) return t("modules.cart.tip.header_dine_in.title");
        return t("modules.cart.tip.header_default.title");
    }
    return (
        <>
            <div className="card-cart">
                <div className={"container-fluid"}>
                    <h5 className={"card-cart-header"}>
                        {renderTipTitle()}
                    </h5>
                    <div className={"card-cart-body"}>
                        <div className="btn-groups-columns tips-container gx-1 gy-1 gap-2">
                            <ResponsiveComponent breakpoint={500} large={renderTips([0, 10, 15, 20, -1])}
                                                 small={renderTips([10, 15, 20, 0, -1])}/>
                        </div>
                    </div>
                </div>
            </div>
            {customAmountModal && <CartTipModal onHide={() => setCustomAmountModal(false)}
                                                handleSave={(tipAmount) => addCustomTip(tipAmount)}
                                                tipAmount={customTip ? tipAmount : undefined} errors={props.errors}
                                                currency={currency}
            />}
        </>
    )
}


const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    cart: getCart(state.cart)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartTipComponent));
